import type { ReactNode } from "react";

import type { ChipClass } from "../shift-readiness.ts";
import { retryLabel, type ViewState } from "../view-state.ts";
import { HeroDisc } from "./Hero.tsx";
import { PhoneShell, type ActionProps, type Banner } from "./PhoneShell.tsx";

/**
 * The states the design does not draw, in the vocabulary the design does.
 *
 * Both driver design files draw eighteen screens and not one of them is a loading, forbidden,
 * conflict or offline state. The rules say to build every designed state and to add nothing that is
 * not in the design, and these four are neither designed nor optional: a phone on a Tanzanian mobile
 * network reaches all of them.
 *
 * So each is rendered as an ordinary screen of this application — the same hero, the same sheet, the
 * same chip, the same 56dp action — rather than in a look nobody drew. A courier sees the furniture
 * they already know whatever has happened, and the words are flagged as this build's so the design
 * team can replace them without hunting. Recorded as Q-59.
 *
 * Three choices in here are worth stating, because each could have gone the convenient way:
 *
 *   - **Loading is not a spinner.** The design has no spinner and a courier reloads constantly. It
 *     is the idle disc with the word `Checking`, which is the quietest thing this vocabulary can say.
 *   - **Forbidden names the scope.** A courier told `driver-app:read` can ask for it by name; one
 *     told "access denied" opens a ticket somebody else has to translate.
 *   - **Offline reuses the design's own banner.** `6 Offline queue` draws an amber strip with
 *     `cloud_off`, and a request that never reached a service is the same fact. That screen is not
 *     built because the queued proof has nowhere to go; the banner it draws is.
 */

export interface StateScreenProps {
  /** The screen family whose stylesheet and hero height this borrows. */
  readonly screen: string;
  /** Omit to draw the explicit chip, title and sub below instead of a `ViewState`. */
  readonly state?: ViewState;
  readonly onRetry?: () => void;
  /** What the screen was trying to do, said in the sub-line of the loading state. */
  readonly purpose?: string;
  readonly chip?: { readonly class: ChipClass; readonly label: string };
  readonly title?: string;
  readonly sub?: string;
  readonly flag?: string;
}

export function StateScreen(props: StateScreenProps): ReactNode {
  const explicit = props.state === undefined;
  const drawn = explicit ? undefined : describe(props.state, props.purpose ?? "");
  const retry = props.state === undefined ? undefined : retryLabel(props.state);

  const actions: ActionProps[] =
    retry === undefined || props.onRetry === undefined
      ? []
      : [{ label: retry, glyph: "refresh", primary: true, onPress: props.onRetry }];

  const chip = props.chip ?? drawn?.chip ?? { class: "unknown" as ChipClass, label: "unknown" };
  const banner: Banner | undefined = drawn?.banner;

  return (
    <PhoneShell
      screen={`${props.screen} screen--state`}
      art={<HeroDisc glyph={drawn?.glyph ?? "help"} caption={drawn?.caption ?? ""} />}
      online={false}
      earningsLabel={null}
      chip={chip}
      title={props.title ?? drawn?.title ?? ""}
      sub={props.sub ?? drawn?.sub ?? ""}
      actions={actions}
      flag={props.flag ?? "wording not in the design"}
      {...(banner === undefined ? {} : { banner })}
    />
  );
}

interface Drawn {
  readonly chip: { readonly class: ChipClass; readonly label: string };
  readonly glyph: string;
  readonly caption: string;
  readonly title: string;
  readonly sub: string;
  readonly banner?: Banner;
}

/**
 * One state, as a screen.
 *
 * Total over `ViewState` rather than defaulted, so a state added to that union fails the build here
 * instead of rendering as a blank sheet.
 */
function describe(state: ViewState, purpose: string): Drawn {
  switch (state.kind) {
    case "loading":
      return {
        chip: { class: "waiting", label: "checking" },
        glyph: "sync",
        caption: "Checking",
        title: "One moment",
        sub: purpose,
      };

    case "ready":
      return {
        chip: { class: "done", label: "ready" },
        glyph: "check_circle",
        caption: "Ready",
        title: "Ready",
        sub: purpose,
      };

    case "offline":
      /*
       * The design's own treatment, borrowed from `6 Offline queue`: the amber strip and the
       * no-signal glyph. A courier in a car park has seen this before.
       */
      return {
        chip: { class: "unknown", label: "no signal" },
        glyph: "cloud_off",
        caption: "No signal",
        title: "We can't reach the service",
        sub: "Nothing has been lost. This will work again when your signal comes back.",
        banner: { glyph: "cloud_off", text: "Offline · nothing could be checked" },
      };

    case "forbidden":
      return {
        chip: { class: "human", label: "action_required" },
        glyph: "lock",
        caption: "Not permitted",
        title: "Your sign-in doesn't allow this",
        sub:
          state.missingScopes.length > 0
            ? `It needs ${state.missingScopes.join(" and ")}. Ask whoever administers your account for that.`
            : "Ask whoever administers your account for access to this.",
      };

    case "error":
      return {
        chip: { class: "closed", label: "failed" },
        glyph: "error",
        caption: "Didn't work",
        title: "That didn't work",
        sub: `${state.message} (${state.code})`,
      };
  }
}
